import { CheckCircle, Paperclip } from 'lucide-react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  gmailDownloadAttachment,
  type GmailDownloadAttachmentResult,
} from 'sprites-types/workflow/tools/gmailDownloadAttachment'
import { FileResult } from '../shared/FileResult'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function GmailDownloadAttachmentDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(gmailDownloadAttachment, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as GmailDownloadAttachmentResult

  const fileName = result.response?.filename || 'attachment'
  const fileUrl = result.response?.source?.value
  const fileSize = undefined
  const mimeType = undefined

  if (fileUrl) {
    return (
      <FileResult
        file={{
          name: fileName,
          url: fileUrl,
          downloadUrl: fileUrl,
          mimeType: mimeType,
          size: fileSize,
        }}
        customLabel="Gmail Attachment Downloaded"
        showPreview={true}
      />
    )
  }

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      <div className="flex items-center gap-4 px-4 py-3">
        <div className="flex-shrink-0">
          <div className="rounded-full bg-green-100 p-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-gray-900">Attachment downloaded</div>
          <div className="text-xs text-gray-600">
            {fileName}
            {fileSize && <span> • {fileSize} bytes</span>}
          </div>
        </div>
        <div className="flex-shrink-0">
          <Paperclip className="h-5 w-5 text-gray-400" />
        </div>
      </div>
    </div>
  )
}

function downloadGmailAttachment(nodeExecution: NodeExecution): Promise<void> {
  const schema = getToolResultSchema(gmailDownloadAttachment, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    console.error('Schema validation failed for download:', parseResult.error)
    return Promise.resolve()
  }

  const result = parseResult.data as GmailDownloadAttachmentResult

  const fileUrl = result.response?.source?.value
  const fileName = result.response?.filename || 'attachment'

  if (!fileUrl) return Promise.resolve()

  return fetch(fileUrl)
    .then((response) => {
      if (!response.ok) throw new Error('Network response was not ok')
      return response.blob()
    })
    .then((blob) => {
      const blobUrl = window.URL.createObjectURL(blob)
      const downloadLink = document.createElement('a')
      downloadLink.href = blobUrl
      downloadLink.download = fileName
      downloadLink.click()
      window.URL.revokeObjectURL(blobUrl)
    })
    .catch((error) => {
      console.error('Download failed:', error)
    })
}

function gmailDownloadAttachmentToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(gmailDownloadAttachment, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data as GmailDownloadAttachmentResult

  const fileName = result.response?.filename || 'attachment'
  const fileUrl = result.response?.source?.value

  return `## Gmail Attachment Downloaded

**File:** ${fileName}
${fileUrl ? `**URL:** ${fileUrl}` : ''}

---
`
}

export const GmailDownloadAttachmentDisplay = createWorkflowToolComponent(
  GmailDownloadAttachmentDisplayComponent,
  downloadGmailAttachment,
  gmailDownloadAttachmentToMarkdown
)
